import { useContext, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import { useDateUtils } from '../../../Utilities/useDateUtils';
import CommentDialog from '../../../Dialogs/CommentDialog';
import CommentsTable from './CommentsTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import useAlert from '../../../Alerts/useAlert';
import ReasonFilter from './CommentsReasonFilter';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { LotRevision } from '../../../Types/LotRevision';
import DateColumn from '../../../DataTable/datatable.datecolumn';

const GradingCommentsSection = memo(function GradingCommentsSection() {

    const { t } = useTranslation();
    const { convertUTCToShortDateTimeSecs } = useDateUtils();
    const {
        lotDetail:
        {
            summary: {
                lotNumber,
                latestRevisionId: currentRevision
            },
            revisions,
            gradingComments
        },
        getLotDetail
    } = useContext(LotDetailContext);

    const getRevision = (lotRevisionId: number) => {
        getLotDetail(lotRevisionId);
    };

    const tableColumns = [
        DateColumn({header: t('LotDetails.GradingComments.CommentDate'), accessor: 'commentDateUtc', includeSeconds:true }),
        {
            Header: t('LotDetails.GradingComments.Account'),
            accessor: 'account'
        },
        {
            Header: t('LotDetails.GradingComments.Reason'),
            accessor: 'reason',
            Filter: ReasonFilter,
            filter: "jsonArray"
        },
        {
            Header: t('LotDetails.GradingComments.Comment'),
            accessor: 'commentText',
            Cell: (props: any) => {
                let comment = props.row.original;

                return (
                    <div className="text-break">
                        {comment.userComment &&
                            <p style={{ whiteSpace: 'pre-wrap' }}>User Comment: {comment.userComment}</p>
                        }
                        {comment.systemComment &&
                            <p style={{ whiteSpace: 'pre-wrap' }}>System Comment: {comment.systemComment}</p>
                        }
                    </div>
                );
            }
        },
        {
            accessor: 'lotRevisionId',
            Cell: ({ row: { original: { lotRevisionId } } }: any) => {

                let revision: LotRevision | undefined = revisions.find(r => r.lotRevisionId === lotRevisionId);

                let title = revision ? `Revision ${lotRevisionId} at ${convertUTCToShortDateTimeSecs(revision.revisionDateUtc)}`
                    : `Revision ${lotRevisionId}`;

                if (lotRevisionId !== currentRevision) {
                    return (
                        <FontAwesomeIcon
                            icon={faClock}
                            className="link"
                            title={title}
                            onClick={() => getRevision(lotRevisionId)} />
                    );
                } else {
                    return "";
                }
            }
        },
        {
            accessor: 'userComment'
        },
        {
            accessor: 'systemComment'
        }
    ];

    const initialState = {
        sortBy: [
            {
                id: 'commentDate',
                desc: true
            }
        ],
        hiddenColumns: ["userComment", "systemComment"]
    };

    return gradingComments?.length > 0
        ? <CommentsTable columns={tableColumns} data={gradingComments} initialState={initialState} addGradingCommentButton={AddGradingComment(lotNumber, getLotDetail)} />
        : <div className="d-flex justify-content-between">
            <p>None</p>
            {AddGradingComment(lotNumber, getLotDetail)}
        </div>;
});

function AddGradingComment(lotNumber: string, getLotDetail: any) {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const [showGradingCommentDialog, setShowGradingCommentDialog] = useState(false);

    const handleGradingCommentSave = (comment: string) => {
        fetchDataAuthenticated(`/api/v1/lot/${lotNumber}/gradingComment`, 'PUT', JSON.stringify(comment))
            .then(async response => {
                if (response.ok) {
                    getLotDetail();
                    setShowGradingCommentDialog(false);
                } else {
                    throw new Error("Failed to add the comment to the lot");
                }
            })
            .catch(error => {
                setShowGradingCommentDialog(false);
                setAlert("danger", "Error", error.message);
            });
    }

    return (
        <>
            <Button variant="primary" onClick={() => setShowGradingCommentDialog(true)}>
                {t('LotDetails.Summary.AddGradingComment')}
            </Button>
            <CommentDialog showDialog={showGradingCommentDialog} closeDialog={() => setShowGradingCommentDialog(false)} onSave={handleGradingCommentSave} />
        </>
    );
}

export default GradingCommentsSection;